import { useMemo, useReducer } from 'react';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import { initialInterruptionsState, interruptionsReducer } from '../../reducer';
import { InterruptionsContextValue } from '../../types';
import useInterruptionsListeners from './useInterruptionsListeners';
import useInterruptionsStateActions from './useInterruptionsStateActions';
import useInterruptionsUiEffects from './useInterruptionsUiEffects';
import useInterruptionTeamsMessage from './interruptionTeamsMessage/useInterruptionTeamsMessage';

type UseInterruptionsControllerParams = Readonly<{
  isAvailabilityEnabled: boolean;
  isAuthenticated: boolean;
  userId: string;
}>;

const useInterruptionsController = ({
  isAvailabilityEnabled,
  isAuthenticated,
  userId,
}: UseInterruptionsControllerParams): InterruptionsContextValue => {
  const [state, dispatch] = useReducer(interruptionsReducer, initialInterruptionsState);
  const { isFeatureEnabled } = useFeatureToggle();
  const enabled = Boolean(
    isAvailabilityEnabled && isAuthenticated && userId && isFeatureEnabled(Features.INTERRUPTIONS),
  );
  const {
    addEmergencyInterruption,
    addInterruption,
    mergeEmergencyInterruptionsSuccess,
    removeEmergencyInterruption,
    removeInterruption,
    reset,
    setEmergencyInterruptionsFailure,
    setEmergencyInterruptionsRequest,
    setEmergencyInterruptionsSuccess,
    setInterruptionResponseStatus,
    setInterruptionsFailure,
    setInterruptionsRequest,
    setInterruptionsSuccess,
    setInterruptionsViewStatus,
  } = useInterruptionsStateActions({ dispatch });

  const { sendInterruptionTeamsResponseMessage } = useInterruptionTeamsMessage();

  useInterruptionsUiEffects({
    enabled,
    reset,
  });

  useInterruptionsListeners({
    addEmergencyInterruption,
    addInterruption,
    currentUserId: userId,
    enabled,
    emergencyInterruptions: state.emergencyInterruptions,
    listInterruptions: state.listInterruptions,
    removeEmergencyInterruption,
    removeInterruption,
    mergeEmergencyInterruptionsSuccess,
    setInterruptionResponseStatus,
    setInterruptionsSuccess,
  });

  return useMemo(
    () => ({
      ...state,
      addEmergencyInterruption,
      addInterruption,
      removeEmergencyInterruption,
      removeInterruption,
      setEmergencyInterruptionsFailure,
      setEmergencyInterruptionsRequest,
      setEmergencyInterruptionsSuccess,
      setInterruptionResponseStatus,
      setInterruptionsFailure,
      setInterruptionsRequest,
      setInterruptionsSuccess,
      setInterruptionsViewStatus,
      sendInterruptionTeamsResponseMessage,
    }),
    [
      addEmergencyInterruption,
      addInterruption,
      removeEmergencyInterruption,
      removeInterruption,
      setEmergencyInterruptionsFailure,
      setEmergencyInterruptionsRequest,
      setEmergencyInterruptionsSuccess,
      setInterruptionResponseStatus,
      setInterruptionsFailure,
      setInterruptionsRequest,
      setInterruptionsSuccess,
      setInterruptionsViewStatus,
      sendInterruptionTeamsResponseMessage,
      state,
    ],
  );
};

export default useInterruptionsController;
